import { calculateExponentialBackoff } from './exponentialBackoff';

export type ReconnectionState = {
  attempt: number;
  nextRetryTime: number | null;
  giveUpTime: number | null;
};

/**
 * Manages reconnection state and logic for WebSocket connections
 * Encapsulates retry attempts, timing, and give-up conditions
 */
export class ReconnectionManager {
  private attempts: number = 0;
  private initialConnectionTime: number | null = null;

  /**
   * Reset all reconnection state (call on successful connection)
   */
  reset(): void {
    this.attempts = 0;
    this.initialConnectionTime = null;
  }

  /**
   * Increment the reconnection attempt counter
   */
  incrementAttempt(): void {
    this.attempts += 1;
  }

  /**
   * Get the current attempt number
   */
  getAttempts(): number {
    return this.attempts;
  }

  /**
   * Initialize connection time tracking if not already set
   */
  startTracking(): void {
    if (this.attempts === 0) {
      this.initialConnectionTime = Date.now();
    }
  }

  /**
   * Check if reconnection attempts should be abandoned
   * @param maxReconnectTime - Maximum time (ms) to keep trying
   * @returns true if should give up, false otherwise
   */
  shouldGiveUp(maxReconnectTime: number): boolean {
    if (!this.initialConnectionTime) {
      return false;
    }

    const now = Date.now();
    const giveUpTime = this.initialConnectionTime + maxReconnectTime;
    return now >= giveUpTime;
  }

  /**
   * Calculate the delay until next retry attempt using exponential backoff
   * @param baseInterval - Base interval in milliseconds
   * @returns Delay in milliseconds with jitter applied
   */
  getNextRetryDelay(baseInterval: number): number {
    return calculateExponentialBackoff(this.attempts, baseInterval);
  }

  /**
   * Get the current reconnection state for UI display
   * @param maxReconnectTime - Maximum time (ms) to keep trying
   * @param nextRetryDelay - Optional delay until next retry (if scheduling)
   * @returns ReconnectionState object
   */
  getReconnectionState(maxReconnectTime: number, nextRetryDelay?: number): ReconnectionState {
    const giveUpTime = this.initialConnectionTime
      ? this.initialConnectionTime + maxReconnectTime
      : null;

    const nextRetryTime = nextRetryDelay
      ? Date.now() + nextRetryDelay
      : null;

    return {
      attempt: this.attempts,
      nextRetryTime,
      giveUpTime
    };
  }

  /**
   * Get the give up time timestamp
   * @param maxReconnectTime - Maximum time (ms) to keep trying
   * @returns Timestamp when reconnection will be abandoned, or null if not tracking
   */
  getGiveUpTime(maxReconnectTime: number): number | null {
    return this.initialConnectionTime
      ? this.initialConnectionTime + maxReconnectTime
      : null;
  }
}
